import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, timeout } from 'rxjs/operators';
import { CellClickedEvent, ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';

interface SettlementPagination {
  total?: number | string | null;
  limit?: number | string | null;
  offset?: number | string | null;
  returned?: number | string | null;
  has_more?: boolean | string | null;
}

interface SettlementSummary {
  settlement_count?: number | string | null;
  complete_count?: number | string | null;
  partial_count?: number | string | null;
  unrated_count?: number | string | null;
  missing_carrier_costs_count?: number | string | null;
  missing_customer_charges_count?: number | string | null;
  carrier_total_cents?: number | string | null;
  customer_total_cents?: number | string | null;
  margin_total_cents?: number | string | null;
  average_margin_percent?: number | string | null;
  negative_margin_count?: number | string | null;
  warning_count?: number | string | null;
  blocking_error_count?: number | string | null;
  needs_attention_count?: number | string | null;
  carrier_confirmed_count?: number | string | null;
  carrier_unconfirmed_count?: number | string | null;
  carrier_audited_count?: number | string | null;
  carrier_unaudited_count?: number | string | null;
  customer_audited_count?: number | string | null;
  customer_unaudited_count?: number | string | null;
  stale_7_day_count?: number | string | null;
}

interface SettlementFacetRow {
  value?: string | null;
  count?: number | string | null;
}

interface SettlementFacets {
  rating_statuses?: SettlementFacetRow[];
  settlement_sources?: SettlementFacetRow[];
  dashboard_statuses?: SettlementFacetRow[];
  import_sources?: SettlementFacetRow[];
}

interface SettlementRow {
  lading_settlement_uuid?: string | null;
  lading_uuid?: string | null;
  alternate_reference?: string | null;
  lading_number?: string | null;
  lading_type?: string | number | null;
  lading_status?: string | null;
  customer_name?: string | null;
  customer_contact_uuid?: string | null;
  carrier_name?: string | null;
  carrier_contact_uuid?: string | null;
  shipper_name?: string | null;
  shipper_locality?: string | null;
  shipper_region?: string | null;
  consignee_name?: string | null;
  consignee_locality?: string | null;
  consignee_region?: string | null;
  settlement_source?: string | null;
  rating_status?: string | null;
  carrier_complete?: boolean | string | null;
  customer_complete?: boolean | string | null;
  margin_complete?: boolean | string | null;
  carrier_header_count?: number | string | null;
  carrier_line_count?: number | string | null;
  carrier_total_cents?: number | string | null;
  carrier_total_amount?: number | string | null;
  any_carrier_audited?: boolean | string | null;
  any_carrier_confirmed?: boolean | string | null;
  customer_header_count?: number | string | null;
  customer_line_count?: number | string | null;
  customer_total_cents?: number | string | null;
  customer_total_amount?: number | string | null;
  any_customer_audited?: boolean | string | null;
  margin_amount_cents?: number | string | null;
  margin_amount?: number | string | null;
  margin_percent?: number | string | null;
  warnings?: unknown[] | string | null;
  blocking_errors?: unknown[] | string | null;
  warning_count?: number | string | null;
  blocking_error_count?: number | string | null;
  has_warnings?: boolean | string | null;
  has_blocking_errors?: boolean | string | null;
  missing_carrier_costs?: boolean | string | null;
  missing_customer_charges?: boolean | string | null;
  has_negative_margin?: boolean | string | null;
  needs_completion?: boolean | string | null;
  needs_attention?: boolean | string | null;
  dashboard_status?: string | null;
  import_source_count?: number | string | null;
  import_sources?: string[] | string | null;
  lading_administration_count?: number | string | null;
  has_lading_administrations?: boolean | string | null;
  has_po_audited?: boolean | string | null;
  has_pod_audited?: boolean | string | null;
  has_confirm_charges?: boolean | string | null;
  has_carrier_invoiced?: boolean | string | null;
  has_freight_cost_approved?: boolean | string | null;
  has_freight_cost_exception?: boolean | string | null;
  has_ready_invoice?: boolean | string | null;
  has_damage_claim?: boolean | string | null;
  has_intent_claim?: boolean | string | null;
  has_claim_review?: boolean | string | null;
  has_customer_invoice_exported?: boolean | string | null;
  has_carrier_invoice_exported?: boolean | string | null;
  has_invoice_thanks?: boolean | string | null;
  has_invoice_due?: boolean | string | null;
  has_invoice_correct?: boolean | string | null;
  has_invoice_late?: boolean | string | null;
  has_carrier_confirmation_sent?: boolean | string | null;
  has_email_to_cons_sent?: boolean | string | null;
  has_pars_sent?: boolean | string | null;
  has_appointment_plus_appointment_made?: boolean | string | null;
  has_appointment_plus_unload_time_set?: boolean | string | null;
  has_scanning_complete?: boolean | string | null;
  appointment_date?: string | null;
  load_start?: string | null;
  load_end?: string | null;
  lading_created_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  days_since_updated?: number | string | null;
  lading_age_days?: number | string | null;
}

interface SettlementLineItem {
  service_name?: string | null;
  service_type?: string | null;
  rate_cents?: number | string | null;
  rate_amount?: number | string | null;
  rate_unit?: string | null;
  multiplier?: number | string | null;
  net_amount_cents?: number | string | null;
  net_amount?: number | string | null;
  charge_mode?: string | null;
  note?: string | null;
  audited?: boolean | string | null;
  created_at?: string | null;
}

interface SettlementCostHeader {
  carrier_name?: string | null;
  customer_name?: string | null;
  subtotal_cents?: number | string | null;
  tax_total_cents?: number | string | null;
  total_cents?: number | string | null;
  audited?: boolean | string | null;
  carrier_confirmed?: boolean | string | null;
  created_at?: string | null;
  line_items?: SettlementLineItem[];
}

interface SettlementDetail extends SettlementRow {
  warnings?: unknown[];
  blocking_errors?: unknown[];
  carrier_costs?: SettlementCostHeader[];
  customer_charges?: SettlementCostHeader[];
}

interface SettlementDashboardResponse {
  success?: boolean;
  code?: string | number;
  message?: string;
  meta?: { as_of?: string | null; domain_uuid?: string | null; include_debug?: boolean | null };
  filters?: unknown;
  pagination?: SettlementPagination;
  summary?: SettlementSummary;
  facets?: SettlementFacets;
  rows?: SettlementRow[];
  data?: SettlementDetail | null;
  warnings?: string[];
}

type BoolFilterKey =
  'needs_attention'
  | 'has_blocking_errors'
  | 'has_warnings'
  | 'missing_carrier_costs'
  | 'missing_customer_charges'
  | 'has_negative_margin'
  | 'needs_completion'
  | 'carrier_confirmed'
  | 'carrier_audited'
  | 'customer_audited';

type CompleteFilterKey = 'carrier_complete' | 'customer_complete' | 'margin_complete';
type SortKey = 'updated_at' | 'created_at' | 'alternate_reference' | 'customer' | 'carrier' | 'customer_total' | 'carrier_total' | 'margin' | 'margin_percent' | 'rating_status' | 'settlement_source' | 'dashboard_status' | 'needs_attention' | 'warning_count' | 'blocking_error_count' | 'days_since_updated' | 'lading_age_days';
type Direction = 'asc' | 'desc';
type ThemeMode = 'dark' | 'light';
type Tone = 'blue' | 'emerald' | 'amber' | 'rose' | 'violet' | 'slate';

interface BooleanFilterConfig {
  key: BoolFilterKey;
  label: string;
  tone: Tone;
}

type SettlementFlagTone = 'clear' | 'error' | 'warning' | 'missing' | 'negative' | 'progress';

interface SettlementFlag {
  label: string;
  tone: SettlementFlagTone;
  title?: string;
}

type SettlementAdminFlagTone = 'sky' | 'amber' | 'emerald' | 'rose' | 'violet' | 'cyan' | 'slate';

interface SettlementAdminFlagConfig {
  field: keyof SettlementRow;
  label: string;
  shortLabel: string;
  tone: SettlementAdminFlagTone;
}

interface SummaryCard {
  label: string;
  value: string;
  sublabel: string;
  tone: Tone;
}

@Component({
  selector: 'app-settlements-dashboard',
  templateUrl: './settlements-dashboard.component.html',
  styleUrls: ['./settlements-dashboard.component.scss']
})
export class SettlementsDashboardComponent implements OnInit {
  private readonly apiUrl = '/api/settlements_dashboard/settlements_dashboard.php';
  private readonly themeStorageKey = 'settlements_dashboard_theme';
  private readonly defaultDateBasis = 'lading_created_at';
  private readonly defaultDateStart = '2026-01-01';
  private readonly listRequestTimeoutMs = 60000;
  private listRequestSequence = 0;
  private listRequestSubscription?: Subscription;

  rows: SettlementRow[] = [];
  gridRows: SettlementRow[] = [];
  private gridApi?: GridApi<SettlementRow>;
  summary: SettlementSummary | null = null;
  facets: SettlementFacets = {};
  pagination: SettlementPagination = {};
  warnings: string[] = [];
  selectedDetail: SettlementDetail | null = null;
  selectedRow: SettlementRow | null = null;

  loading = false;
  detailLoading = false;
  errorMessage = '';
  lastLoadedAt = '';
  loadingMessage = '';
  lastApiRowCount = 0;
  lastApiShape = '';
  themeMode: ThemeMode = this.readStoredTheme();

  searchText = '';
  ratingStatus = '';
  settlementSource = '';
  moneyState = '';
  dateBasis = this.defaultDateBasis;
  dateStart = this.defaultDateStart;
  dateEnd = '';
  staleDays = '';
  minMarginPercent = '';
  maxMarginPercent = '';
  carrierComplete = '';
  customerComplete = '';
  marginComplete = '';
  sort: SortKey = 'updated_at';
  direction: Direction = 'desc';
  limit = 50;
  offset = 0;

  readonly booleanFilters: BooleanFilterConfig[] = [
    { key: 'needs_attention', label: 'Needs attention', tone: 'rose' },
    { key: 'has_blocking_errors', label: 'Blocking errors', tone: 'rose' },
    { key: 'has_warnings', label: 'Warnings', tone: 'amber' },
    { key: 'missing_carrier_costs', label: 'Missing carrier', tone: 'violet' },
    { key: 'missing_customer_charges', label: 'Missing customer', tone: 'violet' },
    { key: 'has_negative_margin', label: 'Negative margin', tone: 'rose' },
    { key: 'needs_completion', label: 'Needs completion', tone: 'amber' },
    { key: 'carrier_confirmed', label: 'Carrier confirmed', tone: 'emerald' },
    { key: 'carrier_audited', label: 'Carrier audited', tone: 'blue' },
    { key: 'customer_audited', label: 'Customer audited', tone: 'blue' },
  ];

  private readonly adminFlagConfigs: SettlementAdminFlagConfig[] = [
    { field: 'has_po_audited', label: 'PO audited', shortLabel: 'PO', tone: 'emerald' },
    { field: 'has_pod_audited', label: 'POD audited', shortLabel: 'POD', tone: 'emerald' },
    { field: 'has_confirm_charges', label: 'Charges confirmed', shortLabel: 'Charges', tone: 'sky' },
    { field: 'has_carrier_invoiced', label: 'Carrier invoiced', shortLabel: 'Carr Inv', tone: 'sky' },
    { field: 'has_freight_cost_approved', label: 'Freight cost approved', shortLabel: 'Cost OK', tone: 'emerald' },
    { field: 'has_freight_cost_exception', label: 'Freight cost exception', shortLabel: 'Cost Ex', tone: 'rose' },
    { field: 'has_ready_invoice', label: 'Ready to invoice', shortLabel: 'Ready Inv', tone: 'emerald' },
    { field: 'has_damage_claim', label: 'Damage claim', shortLabel: 'Damage', tone: 'rose' },
    { field: 'has_intent_claim', label: 'Intent to claim', shortLabel: 'Intent', tone: 'amber' },
    { field: 'has_claim_review', label: 'Claim review', shortLabel: 'Claim Rev', tone: 'amber' },
    { field: 'has_customer_invoice_exported', label: 'Customer invoice exported', shortLabel: 'Cust Exp', tone: 'cyan' },
    { field: 'has_carrier_invoice_exported', label: 'Carrier invoice exported', shortLabel: 'Carr Exp', tone: 'cyan' },
    { field: 'has_invoice_thanks', label: 'Invoice thanks sent', shortLabel: 'Thanks', tone: 'violet' },
    { field: 'has_invoice_due', label: 'Invoice due notice', shortLabel: 'Due', tone: 'amber' },
    { field: 'has_invoice_correct', label: 'Invoice correct', shortLabel: 'Correct', tone: 'emerald' },
    { field: 'has_invoice_late', label: 'Invoice late', shortLabel: 'Late', tone: 'rose' },
    { field: 'has_carrier_confirmation_sent', label: 'Carrier confirmation sent', shortLabel: 'CC Sent', tone: 'sky' },
    { field: 'has_email_to_cons_sent', label: 'Email to consignee sent', shortLabel: 'Cons Email', tone: 'sky' },
    { field: 'has_pars_sent', label: 'PARS sent', shortLabel: 'PARS', tone: 'cyan' },
    { field: 'has_appointment_plus_appointment_made', label: 'Open Dock appointment made', shortLabel: 'OD Appt', tone: 'violet' },
    { field: 'has_appointment_plus_unload_time_set', label: 'Open Dock unload time set', shortLabel: 'OD Time', tone: 'amber' },
    { field: 'has_scanning_complete', label: 'Scanning complete', shortLabel: 'Scanned', tone: 'emerald' },
  ];

  readonly sortOptions: Array<{ value: SortKey; label: string }> = [
    { value: 'updated_at', label: 'Updated' },
    { value: 'created_at', label: 'Settlement created' },
    { value: 'alternate_reference', label: 'Reference' },
    { value: 'customer', label: 'Customer' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'customer_total', label: 'Customer total' },
    { value: 'carrier_total', label: 'Carrier total' },
    { value: 'margin', label: 'Margin' },
    { value: 'margin_percent', label: 'Margin %' },
    { value: 'dashboard_status', label: 'Dashboard status' },
    { value: 'needs_attention', label: 'Needs attention' },
    { value: 'warning_count', label: 'Warnings' },
    { value: 'blocking_error_count', label: 'Blocking errors' },
    { value: 'days_since_updated', label: 'Days stale' },
  ];

  readonly dateBasisOptions = [
    { value: 'updated_at', label: 'Updated' },
    { value: 'created_at', label: 'Settlement created' },
    { value: 'lading_created_at', label: 'Lading created' },
    { value: 'appointment_date', label: 'Appointment' },
    { value: 'load_start', label: 'Load start' },
    { value: 'load_end', label: 'Load end' },
  ];

  readonly boolChoices = [
    { value: '', label: 'Any' },
    { value: 'true', label: 'Complete' },
    { value: 'false', label: 'Open' },
  ];

  readonly boolFilterState: Record<BoolFilterKey, boolean> = {
    needs_attention: false,
    has_blocking_errors: false,
    has_warnings: false,
    missing_carrier_costs: false,
    missing_customer_charges: false,
    has_negative_margin: false,
    needs_completion: false,
    carrier_confirmed: false,
    carrier_audited: false,
    customer_audited: false,
  };


  readonly defaultColDef: ColDef<SettlementRow> = {
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 120,
    flex: 1,
  };

  readonly gridOptions: GridOptions<SettlementRow> = {
    rowHeight: 72,
    headerHeight: 46,
    animateRows: true,
    tooltipShowDelay: 350,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    getRowId: (params) => this.trimmed(params.data.lading_settlement_uuid) || this.trimmed(params.data.lading_uuid) || `${this.trimmed(params.data.alternate_reference)}-${this.trimmed(params.data.updated_at)}`,
  };

  readonly overlayLoadingTemplate = '<span class="settlement-grid-overlay"><i class="fa-solid fa-arrows-rotate fa-spin"></i></span>';
  readonly overlayNoRowsTemplate = '<span class="settlement-grid-overlay">No settlements matched these filters.</span>';

  readonly columnDefs: ColDef<SettlementRow>[] = [
    {
      headerName: 'Reference',
      field: 'alternate_reference',
      minWidth: 190,
      flex: 1.05,
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderReferenceCell(params),
    },
    {
      headerName: 'Customer',
      field: 'customer_name',
      minWidth: 240,
      flex: 1.35,
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderCustomerCell(params),
    },
    {
      headerName: 'Carrier',
      field: 'carrier_name',
      minWidth: 240,
      flex: 1.35,
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderCarrierCell(params),
    },
    {
      headerName: 'Status',
      field: 'dashboard_status',
      minWidth: 160,
      flex: 0.85,
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderStatusCell(params),
    },
    {
      headerName: 'Customer',
      field: 'customer_total_cents',
      type: 'rightAligned',
      minWidth: 130,
      flex: 0.7,
      valueFormatter: (params) => this.formatMoney(params.value),
      cellClass: 'font-mono font-black',
    },
    {
      headerName: 'Carrier',
      field: 'carrier_total_cents',
      type: 'rightAligned',
      minWidth: 130,
      flex: 0.7,
      valueFormatter: (params) => this.formatMoney(params.value),
      cellClass: 'font-mono font-black',
    },
    {
      headerName: 'Margin',
      field: 'margin_amount_cents',
      type: 'rightAligned',
      minWidth: 130,
      flex: 0.7,
      valueFormatter: (params) => this.formatMoney(params.value),
      cellClass: (params) => this.toNumber(params.value) < 0 ? 'settlement-money-negative font-mono font-black' : 'settlement-money-positive font-mono font-black',
    },
    {
      headerName: 'Margin %',
      field: 'margin_percent',
      type: 'rightAligned',
      minWidth: 118,
      flex: 0.62,
      valueFormatter: (params) => params.value === null || params.value === undefined ? 'N/A' : this.formatPercent(params.value),
      cellClass: 'font-mono font-black',
    },
    {
      headerName: 'Flags',
      colId: 'flags',
      minWidth: 260,
      flex: 1.2,
      sortable: false,
      filter: false,
      autoHeight: true,
      cellClass: 'settlement-flags-cell',
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderFlagsCell(params),
    },
    {
      headerName: 'Admin Flags',
      colId: 'lading_admin_flags',
      minWidth: 500,
      flex: 1.65,
      sortable: false,
      filter: false,
      autoHeight: true,
      cellClass: 'settlement-admin-flags-cell',
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderAdminFlagsCell(params),
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      minWidth: 165,
      flex: 0.85,
      sort: 'desc',
      cellRenderer: (params: ICellRendererParams<SettlementRow>) => this.renderUpdatedCell(params),
    },
    {
      headerName: '',
      colId: 'action',
      width: 58,
      minWidth: 58,
      maxWidth: 58,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: () => '<button type="button" class="settlement-grid-action" title="Open financials" aria-label="Open financials"><i class="fa-solid fa-arrow-up-right-from-square"></i></button>',
    },
  ];

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.loadSettlements(true);
  }

  get isDarkTheme(): boolean {
    return this.themeMode === 'dark';
  }

  get themeToggleLabel(): string {
    return this.isDarkTheme ? 'Light' : 'Dark';
  }

  get gridThemeClass(): string {
    return this.isDarkTheme ? 'ag-theme-ulaap-dark' : 'ag-theme-ulaap';
  }

  get totalRows(): number {
    return this.toNumber(this.pagination.total);
  }

  get visibleRows(): SettlementRow[] {
    return Array.isArray(this.gridRows) ? this.gridRows : [];
  }

  get visibleRowCount(): number {
    return Math.max(this.visibleRows.length, this.renderedGridRowCount());
  }

  get hasVisibleRows(): boolean {
    return this.visibleRowCount > 0;
  }

  get showLoadingNotice(): boolean {
    return this.loading && !this.hasVisibleRows;
  }

  get firstVisibleReference(): string {
    const row = this.visibleRows[0] || this.gridApi?.getDisplayedRowAtIndex(0)?.data;
    if (!row) return '';
    return this.trimmed(row.alternate_reference) || this.trimmed(row.lading_number) || this.trimmed(row.lading_settlement_uuid);
  }

  get returnedRows(): number {
    return this.toNumber(this.pagination.returned) || this.visibleRowCount;
  }

  get pageStart(): number {
    return this.totalRows === 0 ? 0 : this.offset + 1;
  }

  get pageEnd(): number {
    return Math.min(this.offset + this.visibleRowCount, this.totalRows);
  }

  get hasPreviousPage(): boolean {
    return this.offset > 0;
  }

  get hasNextPage(): boolean {
    return this.offset + this.visibleRowCount < this.totalRows;
  }

  get activeFilterCount(): number {
    const textFilters = [
      this.searchText,
      this.ratingStatus,
      this.settlementSource,
      this.moneyState,
      this.dateStart,
      this.dateEnd,
      this.staleDays,
      this.minMarginPercent,
      this.maxMarginPercent,
      this.carrierComplete,
      this.customerComplete,
      this.marginComplete,
    ].filter((value) => value.trim() !== '').length;

    const flagFilters = this.booleanFilters.filter((filter) => this.boolFilterState[filter.key]).length;
    return textFilters + flagFilters + (this.dateBasis !== 'updated_at' ? 1 : 0);
  }

  get summaryCards(): SummaryCard[] {
    const summary = this.summary;
    return [
      {
        label: 'Settlements',
        value: this.formatInteger(summary?.settlement_count),
        sublabel: `${this.formatInteger(summary?.complete_count)} complete / ${this.formatInteger(summary?.partial_count)} partial`,
        tone: 'blue',
      },
      {
        label: 'Needs Attention',
        value: this.formatInteger(summary?.needs_attention_count),
        sublabel: `${this.formatInteger(summary?.blocking_error_count)} blocking, ${this.formatInteger(summary?.warning_count)} warnings`,
        tone: 'rose',
      },
      {
        label: 'Customer Billed',
        value: this.formatMoney(summary?.customer_total_cents),
        sublabel: `${this.formatInteger(summary?.missing_customer_charges_count)} missing customer charges`,
        tone: 'emerald',
      },
      {
        label: 'Carrier Cost',
        value: this.formatMoney(summary?.carrier_total_cents),
        sublabel: `${this.formatInteger(summary?.missing_carrier_costs_count)} missing carrier costs`,
        tone: 'violet',
      },
      {
        label: 'Margin',
        value: this.formatMoney(summary?.margin_total_cents),
        sublabel: `${this.formatPercent(summary?.average_margin_percent)} avg / ${this.formatInteger(summary?.negative_margin_count)} losses`,
        tone: this.toNumber(summary?.margin_total_cents) < 0 ? 'rose' : 'emerald',
      },
      {
        label: 'Stale 7+ Days',
        value: this.formatInteger(summary?.stale_7_day_count),
        sublabel: `${this.formatInteger(summary?.carrier_unconfirmed_count)} carrier unconfirmed`,
        tone: 'amber',
      },
    ];
  }

  get ratingStatusOptions(): SettlementFacetRow[] {
    return this.facets.rating_statuses || [];
  }

  get settlementSourceOptions(): SettlementFacetRow[] {
    return this.facets.settlement_sources || [];
  }

  loadSettlements(resetOffset = false): void {
    if (resetOffset) {
      this.offset = 0;
    }

    this.listRequestSubscription?.unsubscribe();
    const requestId = ++this.listRequestSequence;

    this.loading = true;
    this.loadingMessage = '';
    this.errorMessage = '';
    this.syncGridOverlay();

    this.listRequestSubscription = this.http.get<any>(this.apiUrl, { params: this.buildListParams() })
      .pipe(
        timeout(this.listRequestTimeoutMs),
        finalize(() => this.finishListRequest(requestId))
      )
      .subscribe({
        next: (response) => {
          if (!this.isCurrentListRequest(requestId)) return;
          this.applyListResponse(response);
        },
        error: (error: HttpErrorResponse | Error) => {
          if (!this.isCurrentListRequest(requestId)) return;
          this.lastApiRowCount = 0;
          this.lastApiShape = error.name === 'TimeoutError' ? 'timeout' : 'network_error';
          this.errorMessage = error.name === 'TimeoutError'
            ? 'Settlements request timed out. Try fewer filters or refresh again.'
            : (error instanceof HttpErrorResponse ? error.error?.message : '') || 'Network error loading settlements.';
        }
      });
  }

  onGridReady(event: GridReadyEvent<SettlementRow>): void {
    this.gridApi = event.api;
    this.syncGridOverlay();
    window.setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  onGridRowClicked(event: RowClickedEvent<SettlementRow>): void {
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest('.settlement-grid-action')) return;
    if (!event.data) return;
    this.loadDetail(event.data);
  }

  onGridCellClicked(event: CellClickedEvent<SettlementRow>): void {
    if (event.column.getColId() !== 'action' || !event.data) return;
    this.openFinancials(event.data);
  }

  loadDetail(row: SettlementRow): void {
    const settlementUuid = this.trimmed(row.lading_settlement_uuid);
    if (!settlementUuid) {
      return;
    }

    this.selectedRow = row;
    this.selectedDetail = null;
    this.detailLoading = true;

    const params = new HttpParams().set('lading_settlement_uuid', settlementUuid);
    this.http.get<SettlementDashboardResponse>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        this.detailLoading = false;

        if (!this.isSuccess(response) || !response.data) {
          this.errorMessage = response?.message || 'Settlement detail could not be loaded.';
          return;
        }

        this.selectedDetail = response.data;
        this.warnings = Array.isArray(response.warnings) ? response.warnings : this.warnings;
      },
      error: (error: HttpErrorResponse) => {
        this.detailLoading = false;
        this.errorMessage = error.error?.message || 'Network error loading settlement detail.';
      }
    });
  }

  closeDetail(): void {
    this.selectedDetail = null;
    this.selectedRow = null;
    this.detailLoading = false;
  }

  refresh(): void {
    this.loadSettlements(false);
  }

  toggleTheme(): void {
    this.themeMode = this.isDarkTheme ? 'light' : 'dark';
    try {
      localStorage.setItem(this.themeStorageKey, this.themeMode);
    }
    catch (error) {
      // Theme persistence is nice-to-have; rendering should never depend on it.
    }
  }

  themeIconClass(): string {
    return this.isDarkTheme ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  applyFilters(): void {
    this.loadSettlements(true);
  }

  clearDateFilter(): void {
    this.dateBasis = 'updated_at';
    this.dateStart = '';
    this.dateEnd = '';
    this.loadSettlements(true);
  }

  clearFilters(): void {
    this.searchText = '';
    this.ratingStatus = '';
    this.settlementSource = '';
    this.moneyState = '';
    this.applyDefaultDateFilter();
    this.dateEnd = '';
    this.staleDays = '';
    this.minMarginPercent = '';
    this.maxMarginPercent = '';
    this.carrierComplete = '';
    this.customerComplete = '';
    this.marginComplete = '';
    this.booleanFilters.forEach((filter) => this.boolFilterState[filter.key] = false);
    this.loadSettlements(true);
  }

  previousPage(): void {
    if (!this.hasPreviousPage) return;
    this.offset = Math.max(0, this.offset - this.limit);
    this.loadSettlements(false);
  }

  nextPage(): void {
    if (!this.hasNextPage) return;
    this.offset += this.limit;
    this.loadSettlements(false);
  }

  setFlagFilter(key: BoolFilterKey, checked: boolean): void {
    this.boolFilterState[key] = checked;
    this.loadSettlements(true);
  }

  isFlagActive(key: BoolFilterKey): boolean {
    return this.boolFilterState[key];
  }

  setQuickAttention(): void {
    this.booleanFilters.forEach((filter) => this.boolFilterState[filter.key] = false);
    this.boolFilterState.needs_attention = true;
    this.loadSettlements(true);
  }

  setSort(sort: SortKey): void {
    if (this.sort === sort) {
      this.direction = this.direction === 'asc' ? 'desc' : 'asc';
    }
    else {
      this.sort = sort;
      this.direction = sort === 'alternate_reference' || sort === 'customer' || sort === 'carrier' ? 'asc' : 'desc';
    }

    this.loadSettlements(true);
  }

  sortIndicator(sort: SortKey): string {
    if (this.sort !== sort) return '';
    return this.direction === 'asc' ? 'ASC' : 'DESC';
  }

  openFinancials(row: SettlementRow): void {
    const uuid = this.trimmed(row.lading_uuid);
    if (!uuid) return;
    //void this.router.navigate(['/financials', uuid]);
    const urlTree = this.router.createUrlTree(['/financials', uuid]);
    const url = this.router.serializeUrl(urlTree);

    window.open(url, '_blank');
  }

  statusClass(status: string | null | undefined): string {
    const normalized = this.trimmed(status) || 'unknown';
    const base = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-black ring-1';

    if (normalized === 'complete') {
      return this.isDarkTheme
        ? `${base} bg-emerald-400/15 text-emerald-200 ring-emerald-400/30`
        : `${base} bg-emerald-50 text-emerald-700 ring-emerald-200`;
    }

    if (normalized === 'blocked') {
      return this.isDarkTheme
        ? `${base} bg-rose-400/15 text-rose-200 ring-rose-400/30`
        : `${base} bg-rose-50 text-rose-700 ring-rose-200`;
    }

    if (normalized === 'negative_margin') {
      return this.isDarkTheme
        ? `${base} bg-red-400/15 text-red-200 ring-red-400/30`
        : `${base} bg-red-50 text-red-700 ring-red-200`;
    }

    if (normalized === 'warning') {
      return this.isDarkTheme
        ? `${base} bg-amber-400/15 text-amber-200 ring-amber-400/30`
        : `${base} bg-amber-50 text-amber-700 ring-amber-200`;
    }

    if (normalized === 'missing_carrier' || normalized === 'missing_customer' || normalized === 'unrated') {
      return this.isDarkTheme
        ? `${base} bg-violet-400/15 text-violet-200 ring-violet-400/30`
        : `${base} bg-violet-50 text-violet-700 ring-violet-200`;
    }

    return this.isDarkTheme
      ? `${base} bg-sky-400/15 text-sky-200 ring-sky-400/30`
      : `${base} bg-sky-50 text-sky-700 ring-sky-200`;
  }

  cardClass(tone: Tone): string {
    return `settlements-card settlements-card--${tone}`;
  }

  flagClass(tone: Tone, active: boolean): string {
    return `filter-chip filter-chip--${tone}${active ? ' filter-chip--active' : ''}`;
  }

  boolLabel(value: boolean | string | null | undefined): string {
    return this.toBool(value) ? 'Yes' : 'No';
  }

  isTruthy(value: boolean | string | null | undefined): boolean {
    return this.toBool(value);
  }

  hasPositiveCount(value: number | string | null | undefined): boolean {
    return this.toNumber(value) > 0;
  }

  hasRowFlags(row: SettlementRow): boolean {
    return this.hasPositiveCount(row.blocking_error_count)
      || this.hasPositiveCount(row.warning_count)
      || this.isTruthy(row.missing_carrier_costs)
      || this.isTruthy(row.missing_customer_charges);
  }

  formatStatus(status: string | null | undefined): string {
    const value = this.trimmed(status);
    if (!value) return 'Unknown';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatInteger(value: number | string | null | undefined): string {
    return Math.round(this.toNumber(value)).toLocaleString();
  }

  formatMoney(cents: number | string | null | undefined): string {
    const amount = this.toNumber(cents) / 100;
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  formatPercent(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0.00%';
    return `${this.toNumber(value).toFixed(2)}%`;
  }

  formatDateTime(value: string | null | undefined): string {
    const normalized = this.trimmed(value);
    if (!normalized) return 'Not set';
    const date = new Date(normalized.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return normalized;
    return date.toLocaleString();
  }

  location(locality: string | null | undefined, region: string | null | undefined): string {
    return [this.trimmed(locality), this.trimmed(region)].filter(Boolean).join(', ') || 'Location not set';
  }

  importSources(row: SettlementRow): string {
    const sources = row.import_sources;
    if (Array.isArray(sources)) return sources.join(', ');
    return this.trimmed(sources) || 'None';
  }

  costHeaders(headers: SettlementCostHeader[] | undefined): SettlementCostHeader[] {
    return Array.isArray(headers) ? headers : [];
  }

  lineItems(header: SettlementCostHeader): SettlementLineItem[] {
    return Array.isArray(header.line_items) ? header.line_items : [];
  }

  issueList(value: unknown[] | string | null | undefined): string[] {
    return this.issueItems(value);
  }

  trackBySettlement(_: number, row: SettlementRow): string {
    return this.trimmed(row.lading_settlement_uuid) || this.trimmed(row.lading_uuid) || `${row.alternate_reference || ''}`;
  }

  trackByFacet(_: number, row: SettlementFacetRow): string {
    return this.trimmed(row.value) || String(row.count || 'unknown');
  }

  trackByCostHeader(index: number, header: SettlementCostHeader): string {
    return `${index}-${header.created_at || header.carrier_name || header.customer_name || ''}`;
  }

  trackByLineItem(index: number, line: SettlementLineItem): string {
    return `${index}-${line.service_name || line.service_type || line.created_at || ''}`;
  }

  private rowFlags(row: SettlementRow): SettlementFlag[] {
    const flags: SettlementFlag[] = [];

    this.addIssueSummaryFlag({
      flags,
      issues: row.blocking_errors,
      countValue: row.blocking_error_count,
      tone: 'error',
      singularLabel: 'error',
      pluralLabel: 'errors',
      title: 'Settlement has errors; see settlement details to fix.',
    });

    this.addIssueSummaryFlag({
      flags,
      issues: row.warnings,
      countValue: row.warning_count,
      tone: 'warning',
      singularLabel: 'warning',
      pluralLabel: 'warnings',
      title: 'Settlement has potential issues; double check in settlement details before finalizing.',
    });

    if (this.isTruthy(row.missing_carrier_costs)) {
      this.addUniqueFlag(flags, {
        label: this.missingCarrierCostLabel(row),
        tone: 'missing',
        title: 'Carrier cost side is missing a header or line items, so carrier cost and margin may be incomplete.',
      });
    }

    if (this.isTruthy(row.missing_customer_charges)) {
      this.addUniqueFlag(flags, {
        label: this.missingCustomerChargeLabel(row),
        tone: 'missing',
        title: 'Customer charge side is missing a header or line items, so customer billing and margin may be incomplete.',
      });
    }

    if (this.isTruthy(row.has_negative_margin)) {
      const margin = this.toNumber(row.margin_amount_cents);
      this.addUniqueFlag(flags, {
        label: margin < 0 ? `Negative margin ${this.formatMoney(row.margin_amount_cents)}` : 'Negative margin',
        tone: 'negative',
        title: 'Customer total is below carrier total, producing a negative settlement margin.',
      });
    }

    this.addCompletionFlags(flags, row);
    return flags;
  }


  private renderReferenceCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';
    const reference = this.trimmed(row.alternate_reference) || this.trimmed(row.lading_number) || 'No reference';
    const status = this.trimmed(row.lading_status) || 'No lading status';
    return `<div class="settlement-grid-stack"><strong>${this.escapeHtml(reference)}</strong><span>${this.escapeHtml(status)}</span></div>`;
  }

  private renderCustomerCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';
    const customer = this.trimmed(row.customer_name) || 'No customer';
    const shipper = this.trimmed(row.shipper_name) || 'No shipper';
    const location = this.location(row.shipper_locality, row.shipper_region);
    return `<div class="settlement-grid-stack"><strong>${this.escapeHtml(customer)}</strong><span>${this.escapeHtml(shipper)} | ${this.escapeHtml(location)}</span></div>`;
  }

  private renderCarrierCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';
    const carrier = this.trimmed(row.carrier_name) || 'No carrier';
    const consignee = this.trimmed(row.consignee_name) || 'No consignee';
    const location = this.location(row.consignee_locality, row.consignee_region);
    return `<div class="settlement-grid-stack"><strong>${this.escapeHtml(carrier)}</strong><span>${this.escapeHtml(consignee)} | ${this.escapeHtml(location)}</span></div>`;
  }

  private renderStatusCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';
    const status = this.escapeHtml(this.formatStatus(row.dashboard_status));
    const rating = this.trimmed(row.rating_status) || 'No rating';
    const source = this.trimmed(row.settlement_source) || 'No source';
    const meta = this.escapeHtml(`${rating} | ${source}`);
    return `<div class="settlement-grid-stack"><span class="settlement-status-pill settlement-status-pill--${this.statusTone(row.dashboard_status)}">${status}</span><span>${meta}</span></div>`;
  }

  private renderFlagsCell(params: ICellRendererParams<SettlementRow>): string {
    const flags = params.data ? this.rowFlags(params.data) : [];
    if (!flags.length) {
      return '<span class="settlement-flag settlement-flag--clear" title="No settlement flags found.">Clear</span>';
    }

    return `<div class="settlement-grid-flags">${flags.map((flag) => {
      const title = flag.title || flag.label;
      return `<span class="settlement-flag settlement-flag--${flag.tone}" title="${this.escapeHtml(title)}">${this.escapeHtml(flag.label)}</span>`;
    }).join('')}</div>`;
  }

  private renderAdminFlagsCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';

    return `<div class="settlement-admin-flags">${this.adminFlagConfigs.map((flag) => this.renderAdminFlag(flag, row)).join('')}</div>`;
  }

  private renderAdminFlag(flag: SettlementAdminFlagConfig, row: SettlementRow): string {
    const active = this.isTruthy(row[flag.field] as boolean | string | null | undefined);
    const state = active ? 'Yes' : 'No';
    const title = `${flag.label}: ${state}`;
    const classes = [
      'settlement-admin-flag',
      `settlement-admin-flag--${flag.tone}`,
      active ? 'is-on' : 'is-off',
    ].join(' ');

    const iconClass = active ? 'fa-check' : 'fa-minus';
    return `<span class="${classes}" title="${this.escapeHtml(title)}"><span class="settlement-admin-flag__icon"><i class="fa-solid ${iconClass}"></i></span><span class="settlement-admin-flag__label">${this.escapeHtml(flag.shortLabel)}</span></span>`;
  }

  private renderUpdatedCell(params: ICellRendererParams<SettlementRow>): string {
    const row = params.data;
    if (!row) return '';
    return `<div class="settlement-grid-stack"><strong>${this.escapeHtml(this.formatDateTime(row.updated_at))}</strong><span>${this.formatInteger(row.days_since_updated)} days stale</span></div>`;
  }

  private statusTone(status: string | null | undefined): string {
    const normalized = this.trimmed(status);
    if (normalized === 'complete') return 'complete';
    if (normalized === 'blocked') return 'blocked';
    if (normalized === 'negative_margin') return 'negative';
    if (normalized === 'missing_carrier' || normalized === 'missing_customer' || normalized === 'unrated') return 'missing';
    if (normalized === 'warning') return 'warning';
    return 'progress';
  }

  private isCurrentListRequest(requestId: number): boolean {
    return requestId === this.listRequestSequence;
  }

  private finishListRequest(requestId: number): void {
    if (!this.isCurrentListRequest(requestId)) return;
    this.loading = false;
    this.loadingMessage = '';
    this.syncGridOverlay();
  }

  private syncGridOverlay(): void {
    if (!this.gridApi) return;
    if (this.loading && !this.hasVisibleRows) {
      this.gridApi.showLoadingOverlay();
      return;
    }
    if (!this.hasVisibleRows) {
      this.gridApi.showNoRowsOverlay();
      return;
    }
    this.gridApi.hideOverlay();
  }

  private renderedGridRowCount(): number {
    return this.gridApi?.getDisplayedRowCount() || 0;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private addIssueSummaryFlag(args: {
    flags: SettlementFlag[];
    issues: unknown[] | string | null | undefined;
    countValue: number | string | null | undefined;
    tone: SettlementFlagTone;
    singularLabel: string;
    pluralLabel: string;
    title: string;
  }): void {
    const count = Math.max(this.toNumber(args.countValue), this.issueItems(args.issues).length);
    if (count <= 0) return;

    const label = count === 1 ? args.singularLabel : args.pluralLabel;
    this.addUniqueFlag(args.flags, {
      label: `${this.formatInteger(count)} ${label}`,
      tone: args.tone,
      title: args.title,
    });
  }

  private addCompletionFlags(flags: SettlementFlag[], row: SettlementRow): void {
    const initialCount = flags.length;

    if (this.isExplicitlyFalse(row.carrier_complete)) {
      this.addUniqueFlag(flags, {
        label: 'Carrier incomplete',
        tone: 'progress',
        title: 'Carrier costs have not been marked complete yet.',
      });
    }

    if (this.isExplicitlyFalse(row.customer_complete)) {
      this.addUniqueFlag(flags, {
        label: 'Customer incomplete',
        tone: 'progress',
        title: 'Customer charges have not been marked complete yet.',
      });
    }

    if (this.isExplicitlyFalse(row.margin_complete)) {
      this.addUniqueFlag(flags, {
        label: 'Margin incomplete',
        tone: 'progress',
        title: 'Settlement margin has not been marked complete yet.',
      });
    }

    if (this.isTruthy(row.needs_completion) && flags.length === initialCount) {
      this.addUniqueFlag(flags, {
        label: 'Needs completion',
        tone: 'progress',
        title: 'One or more settlement completion checks are still open.',
      });
    }
  }

  private addUniqueFlag(flags: SettlementFlag[], flag: SettlementFlag): void {
    const normalizedLabel = flag.label.trim().toLowerCase();
    if (!normalizedLabel) return;
    if (flags.some((existing) => existing.label.trim().toLowerCase() === normalizedLabel)) return;
    flags.push(flag);
  }

  private missingCarrierCostLabel(row: SettlementRow): string {
    if (this.hasKnownValue(row.carrier_header_count) && this.toNumber(row.carrier_header_count) <= 0) {
      return 'No carrier cost header';
    }

    if (this.hasKnownValue(row.carrier_line_count) && this.toNumber(row.carrier_line_count) <= 0) {
      return 'No carrier line items';
    }

    return 'Missing carrier costs';
  }

  private missingCustomerChargeLabel(row: SettlementRow): string {
    if (this.hasKnownValue(row.customer_header_count) && this.toNumber(row.customer_header_count) <= 0) {
      return 'No customer charge header';
    }

    if (this.hasKnownValue(row.customer_line_count) && this.toNumber(row.customer_line_count) <= 0) {
      return 'No customer line items';
    }

    return 'Missing customer charges';
  }

  private issueItems(value: unknown[] | string | null | undefined): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.issueText(item))
        .filter((item) => item !== '');
    }

    const text = this.stringFromUnknown(value);
    return text ? [text] : [];
  }

  private issueText(item: unknown): string {
    const directText = this.stringFromUnknown(item);
    if (directText) return directText;

    if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const message = this.stringFromUnknown(record['message'])
        || this.stringFromUnknown(record['label'])
        || this.stringFromUnknown(record['description'])
        || this.stringFromUnknown(record['code']);
      if (message) return message;

      try {
        return JSON.stringify(item);
      }
      catch (error) {
        return String(item);
      }
    }

    return '';
  }

  private stringFromUnknown(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return '';
  }

  private isExplicitlyFalse(value: boolean | string | null | undefined): boolean {
    return this.hasKnownValue(value) && !this.toBool(value);
  }

  private hasKnownValue(value: boolean | number | string | null | undefined): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private normalizeListPayload(response: any): SettlementDashboardResponse {
    return response?.body ?? response?.data ?? response ?? {};
  }

  private extractRows(response: any): SettlementRow[] {
    const payload = this.normalizeListPayload(response);
    return Array.isArray(payload?.rows) ? payload.rows : [];
  }

  private applyListResponse(response: any): void {
    const payload = this.normalizeListPayload(response);
    this.loading = false;
    this.loadingMessage = '';
    const topKeys = payload && typeof payload === 'object' ? Object.keys(payload) : [];
    this.lastApiShape = JSON.stringify({
      hasRows: Array.isArray(payload?.rows),
      rows: Array.isArray(payload?.rows) ? payload.rows.length : 0,
      topKeys,
    });

    if (!this.isSuccess(payload)) {
      this.errorMessage = payload.message || 'Settlements could not be loaded.';
      this.syncGridOverlay();
      return;
    }

    const nextRows = this.extractRows(payload);
    this.rows = [...nextRows];
    this.gridRows = [...nextRows];
    this.lastApiRowCount = nextRows.length;
    this.summary = payload.summary || null;
    this.facets = payload.facets || {};
    this.pagination = payload.pagination || {};
    this.warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
    this.lastLoadedAt = payload.meta?.as_of || new Date().toLocaleString();
    this.errorMessage = '';

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.gridRows);
      this.gridApi.refreshCells({ force: true });
    }

    this.syncGridOverlay();
    window.setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  private applyDefaultDateFilter(): void {
    this.dateBasis = this.defaultDateBasis;
    this.dateStart = this.defaultDateStart;
    this.dateEnd = '';
  }

  private buildListParams(): HttpParams {
    let params = new HttpParams()
      .set('limit', String(this.limit))
      .set('offset', String(this.offset))
      .set('sort', this.sort)
      .set('direction', this.direction);

    params = this.setParam(params, 'q', this.searchText);
    params = this.setParam(params, 'rating_status', this.ratingStatus);
    params = this.setParam(params, 'settlement_source', this.settlementSource);
    params = this.setParam(params, 'money_state', this.moneyState);
    params = this.setParam(params, 'date_basis', this.dateBasis);
    params = this.setParam(params, 'date_start', this.dateStart);
    params = this.setParam(params, 'date_end', this.dateEnd);
    params = this.setParam(params, 'stale_days', this.staleDays);
    params = this.setParam(params, 'min_margin_percent', this.minMarginPercent);
    params = this.setParam(params, 'max_margin_percent', this.maxMarginPercent);
    params = this.setParam(params, 'carrier_complete', this.carrierComplete);
    params = this.setParam(params, 'customer_complete', this.customerComplete);
    params = this.setParam(params, 'margin_complete', this.marginComplete);

    this.booleanFilters.forEach((filter) => {
      if (this.boolFilterState[filter.key]) {
        params = params.set(filter.key, 'true');
      }
    });

    return params;
  }

  private readStoredTheme(): ThemeMode {
    try {
      return localStorage.getItem(this.themeStorageKey) === 'light' ? 'light' : 'dark';
    }
    catch (error) {
      return 'dark';
    }
  }

  private setParam(params: HttpParams, key: string, value: string): HttpParams {
    const normalized = value.trim();
    return normalized === '' ? params : params.set(key, normalized);
  }

  private isSuccess(response: SettlementDashboardResponse | null | undefined): boolean {
    if (!response || response.success === false) return false;
    const code = Number(response.code || 200);
    return response.success === true || code < 400;
  }

  private toNumber(value: number | string | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  private toBool(value: boolean | string | null | undefined): boolean {
    if (value === true) return true;
    if (value === false || value === null || value === undefined) return false;
    const normalized = String(value).trim().toLowerCase();
    return ['1', 'true', 't', 'yes', 'y', 'on'].includes(normalized);
  }

  private trimmed(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }
}
